import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const PIPELINE_STAGES = [
  { id: 'nuevo', label: 'Nuevo', color: 'bg-blue-500', icon: 'UserPlus' },
  { id: 'en_cotizacion', label: 'En Cotización', color: 'bg-yellow-500', icon: 'FileText' },
  { id: 'en_espera_autorizacion', label: 'En Espera Autorización', color: 'bg-orange-500', icon: 'Clock' },
  { id: 'autorizado', label: 'Autorizado', color: 'bg-green-500', icon: 'CheckCircle' },
  { id: 'rechazado', label: 'Rechazado', color: 'bg-red-500', icon: 'XCircle' },
  { id: 'cliente_inactivo', label: 'Cliente Inactivo', color: 'bg-gray-500', icon: 'UserX' },
];

export const INTERACTION_TYPES = [
  { id: 'call', label: 'Llamada', icon: 'Phone' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' },
  { id: 'visit', label: 'Visita', icon: 'MapPin' },
  { id: 'followup', label: 'Seguimiento', icon: 'RefreshCw' },
  { id: 'quote_sent', label: 'Cotización Enviada', icon: 'Send' },
  { id: 'approval_pending', label: 'Aprobación Pendiente', icon: 'AlertCircle' },
  { id: 'reminder', label: 'Recordatorio', icon: 'Bell' },
];

export const CRM_WA_TEMPLATES = [
  { id: 'seguimiento', nombre: 'Seguimiento', texto: 'Hola {nombre}, te contactamos de Verdi Llantas para dar seguimiento a tu solicitud. ¿Tienes alguna pregunta o podemos ayudarte en algo más?' },
  { id: 'recordatorio_llamada', nombre: 'Recordatorio de llamada', texto: 'Hola {nombre}, quedamos en hablar hoy. ¿Tienes un momento para platicar sobre el servicio que necesitas?' },
  { id: 'cita', nombre: 'Confirmación de cita', texto: 'Hola {nombre}, te confirmamos tu cita en Verdi Llantas. Por favor avísanos si necesitas reagendar. ¡Te esperamos!' },
  { id: 'cotizacion_enviada', nombre: 'Cotización enviada', texto: 'Hola {nombre}, te hemos enviado tu cotización. Quedo a tus órdenes para cualquier duda. ¿La pudiste revisar?' },
];

export const crmService = {
  async getCompanyId() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;
      const { data } = await supabase
        ?.from('user_profiles')
        ?.select('company_id')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      return data?.company_id || null;
    } catch {
      return null;
    }
  },

  // ─── crm_contacts ──────────────────────────────────────────────────────────
  async getContacts(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('crm_contacts')
        ?.select(`
          *,
          responsable_user:user_profiles!crm_contacts_responsable_fkey(id, full_name),
          activities:crm_activities(id, tipo, fecha, estado, created_at)
        `)
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Failed to fetch CRM contacts:', error);
      return [];
    }
  },

  async createContact(data) {
    try {
      // Sanitize UUID fields: empty strings are invalid UUIDs
      const sanitized = {
        ...data,
        responsable: data?.responsable || null,
        client_id: data?.client_id || null,
        company_id: data?.company_id || null,
      };
      const { data: result, error } = await supabase
        ?.from('crm_contacts')
        ?.insert(sanitized)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }
      return result;
    } catch (error) {
      console.error('Failed to create CRM contact:', error);
      throw error;
    }
  },

  async updateContact(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('crm_contacts')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }
      return data;
    } catch (error) {
      console.error('Failed to update CRM contact:', error);
      throw error;
    }
  },

  // ─── crm_activities ────────────────────────────────────────────────────────
  async getActivities(contactId) {
    try {
      const { data, error } = await supabase
        ?.from('crm_activities')
        ?.select(`
          *,
          responsable_user:user_profiles!crm_activities_responsable_fkey(id, full_name)
        `)
        ?.eq('contact_id', contactId)
        ?.order('fecha', { ascending: false })
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Failed to fetch CRM activities:', error);
      return [];
    }
  },

  async createActivity(data) {
    try {
      // contact_id es NOT NULL — validar antes de insertar
      const contactId = data?.contact_id || null;
      if (!contactId) {
        throw new Error('El contacto es requerido para registrar una actividad');
      }
      // Sanitizar campos UUID opcionales: cadenas vacías son UUIDs inválidos
      const sanitized = {
        contact_id: contactId,
        tipo: data?.tipo,
        fecha: data?.fecha,
        hora: data?.hora || null,
        estado: data?.estado || 'pendiente',
        notas: data?.notas || null,
        responsable: data?.responsable || null,
        company_id: data?.company_id || null,
      };
      const { data: result, error } = await supabase
        ?.from('crm_activities')
        ?.insert(sanitized)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }
      return result;
    } catch (error) {
      console.error('Failed to create CRM activity:', error);
      throw error;
    }
  },

  async updateActivity(id, updates) {
    try {
      const { data, error } = await supabase
        ?.from('crm_activities')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }
      return data;
    } catch (error) {
      console.error('Failed to update CRM activity:', error);
      throw error;
    }
  },

  async getActivitiesForAgenda(companyId) {
    try {
      const { data, error } = await supabase
        ?.from('crm_activities')
        ?.select('*, contact:crm_contacts(nombre, telefono, whatsapp), responsable_user:user_profiles!crm_activities_responsable_fkey(id, full_name)')
        ?.eq('company_id', companyId)
        ?.order('fecha', { ascending: true })
        ?.order('hora', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Failed to fetch agenda activities:', error);
      return [];
    }
  },

  // ─── convertToClient ───────────────────────────────────────────────────────
  async convertToClient(contactId, companyId) {
    try {
      // 1. Get contact data
      const { data: contact, error: fetchError } = await supabase
        ?.from('crm_contacts')
        ?.select('*')
        ?.eq('id', contactId)
        ?.single();

      if (fetchError) throw new Error(fetchError.message);

      // 2. Insert into clients
      const { data: newClient, error: clientError } = await supabase
        ?.from('clients')
        ?.insert({
          nombre: contact?.nombre,
          empresa: contact?.empresa || null,
          telefono: contact?.telefono || '',
          email: contact?.email || null,
          company_id: companyId,
          notas: contact?.notas || null,
        })
        ?.select()
        ?.single();

      if (clientError) throw new Error(clientError.message);

      // 3. Update crm_contact with client_id and etapa
      const { error: updateError } = await supabase
        ?.from('crm_contacts')
        ?.update({ client_id: newClient?.id, etapa: 'cerrado_ganado', updated_at: new Date()?.toISOString() })
        ?.eq('id', contactId);

      if (updateError) throw new Error(updateError.message);

      return newClient;
    } catch (error) {
      console.error('Failed to convert contact to client:', error);
      throw error;
    }
  },

  // ─── Existing crm_interactions methods (kept intact) ──────────────────────
  async createInteraction(interactionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Usuario no autenticado');

      const companyId = await this.getCompanyId();

      const snakeData = toSnakeCase({
        ...interactionData,
        companyId,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('crm_interactions')
        ?.insert(snakeData)
        ?.select(`
          *,
          client:clients(id, nombre, telefono, email),
          vehicle:vehicles(id, marca, modelo, year, placas)
        `)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create CRM interaction:', error);
      throw error;
    }
  },

  async updateInteraction(interactionId, updates) {
    try {
      const snakeData = toSnakeCase({ ...updates, updatedAt: new Date()?.toISOString() });
      const { data, error } = await supabase
        ?.from('crm_interactions')
        ?.update(snakeData)
        ?.eq('id', interactionId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update CRM interaction:', error);
      throw error;
    }
  },

  async getPipeline() {
    try {
      const companyId = await this.getCompanyId();

      let query = supabase
        ?.from('crm_interactions')
        ?.select(`
          *,
          client:clients(id, nombre, empresa, telefono, email),
          vehicle:vehicles(id, marca, modelo, year, placas)
        `)
        ?.eq('status', 'open')
        ?.order('created_at', { ascending: false });

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      const interactions = toCamelCase(data || []);

      // Group by pipeline stage
      const pipeline = {};
      PIPELINE_STAGES?.forEach(stage => {
        pipeline[stage?.id] = interactions?.filter(i => i?.pipelineStage === stage?.id);
      });

      return pipeline;
    } catch (error) {
      console.error('Failed to fetch CRM pipeline:', error);
      return {};
    }
  },

  async getByClient(clientId) {
    try {
      const { data, error } = await supabase
        ?.from('crm_interactions')
        ?.select(`
          *,
          vehicle:vehicles(id, marca, modelo, year, placas),
          created_by_user:user_profiles!crm_interactions_created_by_fkey(full_name)
        `)
        ?.eq('client_id', clientId)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch client CRM interactions:', error);
      return [];
    }
  },

  async getPendingActions() {
    try {
      const companyId = await this.getCompanyId();
      const now = new Date()?.toISOString();

      let query = supabase
        ?.from('crm_interactions')
        ?.select(`
          *,
          client:clients(id, nombre, telefono, email)
        `)
        ?.eq('status', 'open')
        ?.not('next_action_date', 'is', null)
        ?.lte('next_action_date', new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString())
        ?.order('next_action_date', { ascending: true });

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch pending actions:', error);
      return [];
    }
  }
};
